import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const ScrollIndicatorWrapper = ({ children, sx = {}, containerSx = {}, disabled = false, resetScrollDeps = [], ...props }) => {
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollElementRef = useRef(null);

  const checkScroll = useCallback(() => {
    if (disabled) {
      setShowScrollIndicator(false);
      return;
    }
    const el = scrollElementRef.current;
    if (!el) return;
    const isScrollable = el.scrollHeight > el.clientHeight;
    // Buffer of 5px to prevent flickering at exact bottom
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 5;

    if (showScrollIndicator && isAtBottom) {
      setShowScrollIndicator(false);
    } else if (!showScrollIndicator && !isAtBottom && isScrollable) {
      setShowScrollIndicator(true);
    }
  }, [showScrollIndicator, disabled]);

  // Handle scroll reset based on dependencies
  useEffect(() => {
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollTop = 0;
      checkScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetScrollDeps);

  useEffect(() => {
    const el = scrollElementRef.current;
    if (!el) return;

    checkScroll();
    
    // Check on resize (window or element)
    const handleResize = () => checkScroll();
    window.addEventListener('resize', handleResize);
    
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    // Observe child to detect content changes (like accordions opening)
    if (el.firstElementChild) {
        observer.observe(el.firstElementChild);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [children, checkScroll]);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...containerSx
      }}
    >
      <Box
        ref={scrollElementRef}
        onScroll={checkScroll}
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100%',
          width: '100%',
          ...sx
        }}
        {...props}
      >
        {children}
      </Box>
      {showScrollIndicator && !disabled && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30px',
            textAlign: 'center',
            pointerEvents: 'none',
            background: (theme) => `linear-gradient(to top, ${theme.palette.background.paper} 40%, transparent)`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            pb: 0.5,
            zIndex: 2,
          }}
        >
          <ArrowDownwardIcon
            color="primary"
            sx={{
              fontSize: '1.2rem',
              animation: 'bounce 2s ease-in-out 1',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': { transform: 'translateY(0)' },
                '40%': { transform: 'translateY(-6px)' },
                '60%': { transform: 'translateY(-3px)' },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

ScrollIndicatorWrapper.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  containerSx: PropTypes.object,
  disabled: PropTypes.bool,
  resetScrollDeps: PropTypes.array,
};

export default ScrollIndicatorWrapper;